// components/seo/ArticleSEO.tsx
import { Platform } from 'react-native';
import Head from 'expo-router/head';

interface ArticleSEOProps {
  title: string;
  description: string;
  keywords: string[];
  author?: string;
  publishedAt: string;
  slug: string;
  imageUrl?: string;
}

export function ArticleSEO({
  title,
  description,
  keywords,
  author = 'Kursi',
  publishedAt,
  slug,
  imageUrl = 'https://kursiaii.com/og-image.webp',
}: ArticleSEOProps) {
  if (Platform.OS !== 'web') return null;

  const siteUrl = 'https://kursiaii.com';
  const url = `${siteUrl}/articles/${slug}`;
  const cleanTitle = (title || '').trim();
  const cleanDesc = (description || '').trim();
  const cleanKeywords = Array.isArray(keywords) ? keywords.filter(Boolean) : [];

  // SEO/GEO FIX: Use BlogPosting + BreadcrumbList for richer, valid article extraction in search and AI engines.
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: cleanTitle,
    description: cleanDesc,
    inLanguage: 'he-IL',
    author: {
      '@type': 'Person',
      name: author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Kursi',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/apple-touch-icon.png`,
      },
    },
    datePublished: publishedAt,
    dateModified: publishedAt,
    image: [imageUrl],
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    keywords: cleanKeywords.join(', '),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'דף הבית', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'מאמרים', item: `${siteUrl}/articles` },
      { '@type': 'ListItem', position: 3, name: cleanTitle, item: url },
    ],
  };

  return (
    <Head>
      <title key="title">{cleanTitle}</title>

      <meta key="meta:description" name="description" content={cleanDesc} />
      <meta key="meta:keywords" name="keywords" content={cleanKeywords.join(', ')} />
      <meta key="meta:author" name="author" content={author} />
      <meta key="meta:robots" name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />

      <link key="link:canonical" rel="canonical" href={url} />

      <meta key="og:type" property="og:type" content="article" />
      <meta key="og:title" property="og:title" content={cleanTitle} />
      <meta key="og:description" property="og:description" content={cleanDesc} />
      <meta key="og:url" property="og:url" content={url} />
      <meta key="og:image" property="og:image" content={imageUrl} />
      <meta key="og:site_name" property="og:site_name" content="Kursi" />
      <meta key="article:published_time" property="article:published_time" content={publishedAt} />

      <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
      <meta key="twitter:title" name="twitter:title" content={cleanTitle} />
      <meta key="twitter:description" name="twitter:description" content={cleanDesc} />
      <meta key="twitter:image" name="twitter:image" content={imageUrl} />

      <script
        key="ldjson:article"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <script
        key="ldjson:breadcrumb"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </Head>
  );
}
